import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import AdminShell from "@/components/AdminShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminProjects() {
    const { user, token, loading } = useAuth();
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState({});
    const [busy, setBusy] = useState(true);

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token],
    );

    useEffect(() => {
        if (!token) return;
        Promise.all([
            axios.get(`${API}/admin/projects`, { headers }),
            axios.get(`${API}/admin/clients`, { headers }),
        ])
            .then(([p, c]) => {
                setItems(p.data);
                const map = {};
                c.data.forEach((cl) => (map[cl.id] = cl));
                setClients(map);
            })
            .finally(() => setBusy(false));
    }, [token, headers]);

    if (loading) return null;
    if (!user) return <Navigate to="/admin/login" replace />;

    return (
        <AdminShell>
            <div className="mb-8">
                <div className="text-[10px] uppercase tracking-[0.25em] text-ember mb-2">
                    Pipeline
                </div>
                <h1 className="font-display text-bone text-4xl tracking-tightest leading-[1]">
                    Projects
                </h1>
            </div>
            {busy ? (
                <Loader2 className="w-5 h-5 animate-spin text-ember" />
            ) : items.length === 0 ? (
                <div className="border border-white/10 bg-ink-800/40 p-12 text-center text-bone-300">
                    No projects yet. Create one from a client's profile.
                </div>
            ) : (
                <ul className="border-t border-white/10">
                    {items.map((p) => (
                        <li key={p.id} className="border-b border-white/10">
                            <Link
                                to={`/admin/projects/${p.id}`}
                                data-testid={`admin-project-${p.id}`}
                                className="grid grid-cols-12 gap-4 items-center py-4 px-2 hover:bg-white/5"
                            >
                                <span className="col-span-5 font-display text-bone text-lg tracking-tight">
                                    {p.title}
                                </span>
                                <span className="col-span-3 text-bone-300 text-sm">
                                    {clients[p.client_id]?.name || "—"}
                                </span>
                                <span className="col-span-2 text-xs uppercase tracking-[0.2em] text-bone-500">
                                    {new Date(p.created_at).toLocaleDateString()}
                                </span>
                                <span className="col-span-2 justify-self-end text-[10px] uppercase tracking-[0.2em] text-ember border border-ember/40 px-2 py-1">
                                    {p.status}
                                </span>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </AdminShell>
    );
}
